import { type IPrimaryCategoryRepository } from '@webapp/core/business-logic/interfaces/IPrimaryCategoryRepository'
import { type ISecondaryCategoryRepository } from '@webapp/core/business-logic/interfaces/ISecondaryCategoryRepository'
import { RequestTypes } from '@webapp/core/entities/internal/enums/RequestTypeEnum'
import { PrimaryCategory } from '@webapp/core/entities/internal/PrimaryCategory'
import { SecondaryCategory } from '@webapp/core/entities/internal/SecondaryCategory'

const PAGE_SIZE = 999

export interface GetTravelRequestFormDataParams {
  userPrimaryCategoryId: Nullable<number>
  userSecondaryCategoryId: Nullable<number>
}

export interface TravelRequestFormData {
  locations: PrimaryCategory[]
  /** Travel-requestable departments of the default location */
  departments: SecondaryCategory[]
  defaultLocation: Nullable<PrimaryCategory>
  defaultDepartment: Nullable<SecondaryCategory>
}

interface Dependency {
  primaryCategoryRepository: IPrimaryCategoryRepository
  secondaryCategoryRepository: ISecondaryCategoryRepository
}

/**
 * Hydrates the create-travel-request form: travel-requestable locations,
 * the departments of the resolved default location, and the defaults
 * (user's location/department when requestable, else first).
 */
export class GetTravelRequestFormDataUseCase {
  constructor(private readonly deps: Dependency) {}

  execute = async ({
    userPrimaryCategoryId,
    userSecondaryCategoryId,
  }: GetTravelRequestFormDataParams): Promise<TravelRequestFormData> => {
    const locationsResponse = await this.deps.primaryCategoryRepository.list({
      requestType: RequestTypes.TRAVEL,
      orderBy: 'name',
      pageSize: PAGE_SIZE,
    })

    const locations = locationsResponse.data.map(
      (location) => new PrimaryCategory(location)
    )

    const defaultLocation =
      locations.find((location) => location.id === userPrimaryCategoryId) ??
      locations[0] ??
      null

    let departments: SecondaryCategory[] = []
    let defaultDepartment: Nullable<SecondaryCategory> = null

    if (defaultLocation) {
      const departmentsResponse =
        await this.deps.secondaryCategoryRepository.list({
          primaryCategoryId: defaultLocation.id,
          requestType: RequestTypes.TRAVEL,
          orderBy: 'name',
          pageSize: PAGE_SIZE,
        })

      departments = departmentsResponse.data.map(
        (department) => new SecondaryCategory(department)
      )

      defaultDepartment =
        departments.find(
          (department) => department.id === userSecondaryCategoryId
        ) ??
        departments[0] ??
        null
    }

    return {
      locations,
      departments,
      defaultLocation,
      defaultDepartment,
    }
  }
}
